import { type ReactNode, useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { SalePurchase, SalesStatusFilter, SharedProps } from '@/types'

const STATUS_LABELS: Record<SalesStatusFilter, string> = {
  all: 'All',
  paid: 'Paid',
  refunded: 'Refunded',
  partially_refunded: 'Partially refunded',
  failed: 'Failed',
  pending: 'Pending',
  expired: 'Expired',
}

const STATUS_BADGE_VARIANT: Record<
  SalePurchase['status'],
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  paid: 'default',
  refunded: 'destructive',
  partially_refunded: 'secondary',
  failed: 'destructive',
  pending: 'outline',
  expired: 'outline',
}

type SalesPageProps = {
  event: { id: string; slug: string; title: string }
  purchases: SalePurchase[]
  status_filter: SalesStatusFilter
  status_options: SalesStatusFilter[]
}

function formatDateTime(value: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`
}

export default function EventSales({ event, purchases, status_filter, status_options }: SalesPageProps) {
  const { flash } = usePage<SharedProps>().props
  const [refundTarget, setRefundTarget] = useState<SalePurchase | null>(null)

  const handleFilter = (status: SalesStatusFilter) => {
    router.get(`/dashboard/events/${event.id}/sales`, status === 'all' ? {} : { status })
  }

  const isFiltered = status_filter !== 'all'

  return (
    <>
      <Head title={`Sales · ${event.title}`} />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <Link
              href={`/dashboard/events/${event.id}/edit`}
              className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" /> Back to event
            </Link>
            <h1 className="text-4xl font-semibold tracking-tight">Sales</h1>
            <p className="mt-1 text-muted-foreground">{event.title}</p>
          </div>
        </div>

        {flash?.success && (
          <div className="mb-6 rounded-md border border-green-500/50 bg-green-500/10 p-3 text-base text-green-900">
            {flash.success}
          </div>
        )}
        {flash?.alert && (
          <div className="mb-6 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-base text-destructive">
            {flash.alert}
          </div>
        )}

        <div className="mb-6 flex flex-wrap gap-2">
          {status_options.map(status => {
            const isActive = status_filter === status
            return (
              <button
                key={status}
                type="button"
                onClick={() => handleFilter(status)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium transition ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70'
                }`}
              >
                {STATUS_LABELS[status]}
              </button>
            )
          })}
        </div>

        {purchases.length === 0 ? (
          <div className="rounded-2xl border border-black/5 bg-muted/30 p-10 text-center">
            <p className="text-base text-muted-foreground">
              {isFiltered
                ? `No purchases match the "${STATUS_LABELS[status_filter]}" filter.`
                : 'No purchases yet. Once buyers complete checkout for this event, they’ll appear here.'}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {purchases.map(purchase => (
                  <TableRow key={purchase.id}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatDateTime(purchase.paid_at ?? purchase.created_at)}
                    </TableCell>
                    <TableCell>
                      {purchase.buyer ? (
                        <div>
                          {purchase.buyer.name && (
                            <div className="font-medium">{purchase.buyer.name}</div>
                          )}
                          <div className="text-sm text-muted-foreground">{purchase.buyer.email}</div>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <ul className="space-y-0.5 text-sm">
                        {purchase.line_items.map((li, idx) => (
                          <li key={idx} className="text-muted-foreground">
                            {li.tier_name ?? 'Item'} &times; {li.quantity}
                          </li>
                        ))}
                        {purchase.line_items.length === 0 && (
                          <li className="text-muted-foreground capitalize">{purchase.kind}</li>
                        )}
                      </ul>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-medium">{formatMoney(purchase.amount_cents)}</div>
                      {purchase.processing_fee_cents > 0 && (
                        <div className="text-xs text-muted-foreground">
                          Card processing fee{purchase.fee_covered_by_donor ? ' (covered by donor)' : ''}:{' '}
                          {formatMoney(purchase.processing_fee_cents)}
                        </div>
                      )}
                      {purchase.refunded_amount_cents > 0 && (
                        <div className="text-xs text-muted-foreground">
                          Refunded {formatMoney(purchase.refunded_amount_cents)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGE_VARIANT[purchase.status]}>
                        {STATUS_LABELS[purchase.status as SalesStatusFilter] ?? purchase.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {purchase.refundable && (
                        <Button size="sm" variant="outline" onClick={() => setRefundTarget(purchase)}>
                          Refund
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <RefundDialog
        eventId={event.id}
        purchase={refundTarget}
        onClose={() => setRefundTarget(null)}
      />
    </>
  )
}

type RefundDialogProps = {
  eventId: string
  purchase: SalePurchase | null
  onClose: () => void
}

function RefundDialog({ eventId, purchase, onClose }: RefundDialogProps) {
  const [partial, setPartial] = useState(false)
  const [amountDollars, setAmountDollars] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const open = purchase !== null
  const remainingCents = purchase?.remaining_refundable_cents ?? 0
  const remainingDollars = (remainingCents / 100).toFixed(2)

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setPartial(false)
      setAmountDollars('')
      onClose()
    }
  }

  const partialCents = (() => {
    const parsed = Number.parseFloat(amountDollars)
    if (!Number.isFinite(parsed) || parsed <= 0) return null
    const cents = Math.round(parsed * 100)
    if (cents > remainingCents) return null
    return cents
  })()
  const submitDisabled = submitting || (partial && partialCents === null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!purchase) return

    const cents = partial ? partialCents : remainingCents
    if (cents === null) return

    setSubmitting(true)
    router.post(
      `/dashboard/events/${eventId}/sales/${purchase.id}/refund`,
      { amount_cents: cents },
      {
        preserveScroll: true,
        onFinish: () => {
          setSubmitting(false)
          handleOpenChange(false)
        },
      }
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {purchase?.kind === 'donation' ? 'Refund this donation?' : 'Refund this purchase?'}
          </DialogTitle>
          <DialogDescription>
            Refunds reduce your Stripe balance and refund the {purchase?.kind === 'donation' ? 'donor' : 'buyer'}&apos;s card.
            {purchase?.kind === 'ticket' && ' A full refund frees the seats so they can be resold.'}
            {(purchase?.kind === 'sponsorship' || purchase?.kind === 'ad') &&
              ' A full refund frees the slot so it can be resold.'}
          </DialogDescription>
        </DialogHeader>

        {purchase && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="rounded-lg border border-black/5 bg-muted/30 p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Remaining refundable</span>
                <span className="font-medium">${remainingDollars}</span>
              </div>
              {purchase.refunded_amount_cents > 0 && (
                <div className="mt-1 flex items-center justify-between text-muted-foreground">
                  <span>Already refunded</span>
                  <span>{formatMoney(purchase.refunded_amount_cents)}</span>
                </div>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={partial}
                onChange={e => setPartial(e.target.checked)}
              />
              Refund a partial amount
            </label>

            {partial && (
              <div className="space-y-1.5">
                <Label htmlFor="refund-amount">Refund amount (USD)</Label>
                <Input
                  id="refund-amount"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0.01"
                  max={remainingDollars}
                  value={amountDollars}
                  onChange={e => setAmountDollars(e.target.value)}
                  placeholder={`Up to $${remainingDollars}`}
                />
                {amountDollars !== '' && partialCents === null && (
                  <p className="text-sm text-destructive">
                    Enter an amount between $0.01 and ${remainingDollars}.
                  </p>
                )}
              </div>
            )}

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="ghost" disabled={submitting}>
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={submitDisabled}>
                {partial
                  ? `Refund $${amountDollars || '0.00'}`
                  : `Refund full $${remainingDollars}`}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

EventSales.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
